import { z } from 'zod';
import { getSiteWidgetExtensionsDir } from '@wix/cli-astro-definitions';
import {
  folderExistsMessage,
  pathAvailableValidation,
} from '../../../dev-center/components/validations/common.js';
import { createCustomElementFolderValidation } from '../../../dev-center/components/validations/custom-element.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { enrichCustomElementWithDerivedFolder } from '../utils/derive-folder.js';
import type { WithDerivedFolder } from '../utils/derive-folder.js';
import { generateCustomElement } from './generate.js';

const schema = z.object({
  extensionType: z.literal('CUSTOM_ELEMENT'),
  name: z.string().describe(defineField({ title: 'Widget name' })),
  folder: z
    .string()
    .regex(/^[a-z][a-z0-9]*(-[a-z0-9]+)+$/)
    .optional()
    .describe(
      defineField({
        title: 'Widget folder',
        description:
          "Must be a valid custom element name: lowercase, starts with a letter, contains at least one hyphen. Derived from the widget name when omitted (suffixed with '-element' when needed).",
      })
    ),
});

export const customElement: ExtensionDef<
  typeof schema,
  WithDerivedFolder<typeof schema>
> = {
  title: 'Custom Element',
  description: 'A site widget built as a custom HTML element',
  group: 'Site',
  documentation:
    'https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/site-widgets/add-a-site-widget-extension',
  zodSchema: schema,
  enrich: (data) =>
    enrichCustomElementWithDerivedFolder(data, {
      sourceField: 'name',
      suffix: 'element',
    }),
  validate: async (projectFolder, data) => {
    const dir = getSiteWidgetExtensionsDir(projectFolder);
    if (!pathAvailableValidation(dir)(data.folder)) {
      return folderExistsMessage('widget', data.folder, 'name');
    }
    const result = createCustomElementFolderValidation(dir)(data.folder);
    return result.valid ? null : result.reason;
  },
  generate: ({ projectFolder, data }) =>
    generateCustomElement(projectFolder, data.folder, data.name),
};
